import React, { useState, useEffect, useContext } from 'react';
import { SimplifiedChampion, SimplifiedRunePage } from '../../XXX/gameTypes';
import AbilityDisplays from '../../XXX/GameSimulator/GameContainer2/GameGrid/ChampionCard/AbilityButtons/AbilityDisplays';
import MinimalRuneDisplay from '../../XXX/GameSimulator/GameContainer2/GameGrid/ChampionCard/Runes/MinimalRuneDisplay';
import InventoryDisplay from '../../XXX/GameSimulator/GameContainer2/GameGrid/ChampionCard/Items/InventoryDisplay';
import ChampionImageWithLevel from '../../XXX/GameSimulator/GameContainer2/GameGrid/ChampionCard/HealthBar/ChampionImageWithLevel';
import SimpleHealthManaBar from '../../XXX/GameSimulator/GameContainer2/GameGrid/ChampionCard/HealthBar/SimpleHealthManaBar';
import GameAssetsContext from '../../XXX/context/GameAssetContext';
import { ShardNames } from '../../XXX/BuildOverview/runeConfig';
//import './StaticChampionCard.css';
import CategoricalSettingsView from '../../XXX/GameSimulator/GameContainer2/GameGrid/ChampionCard/SettingsPanel/CategoricalSettingsView';
import StatsDisplay2 from '../../XXX/GameSimulator/GameContainer2/GameGrid/ChampionCard/StatsDisplay/StatsDisplay2';
import StatsDisplayExpanded from '../../XXX/GameSimulator/GameContainer2/GameGrid/ChampionCard/StatsDisplay/StatsDisplayExpanded';
import { ChampionConfig } from '../../XXX/GameSimulator/GameContainer2/GameGrid/ChampionCard/SettingsPanel/settings';
import StaticSimpleMatchupDisplay from './StaticSimpleMatchupDisplay';
import DataService from '../../XXX/DataService';

import './StaticChampionCardNew.css';

// Cached version for synchronous access (will be populated after first async fetch)
let cachedApiVersion = '15.23.1';

// Helper function to get summoner spell image URL
export const getSummonerSpellImageUrl = (spellName: string): string => {
  const spellKeyMap: Record<string, string> = {
    "Flash": "SummonerFlash",
    "Teleport": "SummonerTeleport",
    "Ignite": "SummonerDot",
    "Exhaust": "SummonerExhaust",
    "Cleanse": "SummonerBoost",
    "Ghost": "SummonerHaste",
    "Smite": "SummonerSmite",
    "Heal": "SummonerHeal",
    "Barrier": "SummonerBarrier"
  };
  
  const spellKey = spellKeyMap[spellName] || "SummonerFlash"; // Default to Flash if not found
  return `https://ddragon.leagueoflegends.com/cdn/${cachedApiVersion}/img/spell/${spellKey}.png`;
};

// Initialize the cached version
DataService.fetchLatestVersion().then(version => {
  cachedApiVersion = version;
});

interface StaticChampionCardProps {
  champion: SimplifiedChampion;
  regularLogs?: any[]; // Optional for compatibility
  comboLogs?: any[]; // Optional for compatibility  
  targetChampion?: SimplifiedChampion | null; // Optional for compatibility
  onSelectTargetClick?: (championId: string) => void; // Optional for compatibility
  onChampionUpdate?: (updatedChampion: SimplifiedChampion) => void; // For handling champion updates
  defaultShowCombos?: boolean; // When true, combos section is open by default regardless of level
}

const StaticChampionCard: React.FC<StaticChampionCardProps> = ({ 
  champion, 
  regularLogs = [], 
  comboLogs = [], 
  targetChampion = null, 
  onSelectTargetClick = () => {},
  onChampionUpdate = () => {},
  defaultShowCombos,
}) => {

  // Determine view state based on champion level
  const shouldAutoOpenDamageTab = defaultShowCombos ?? [1, 2, 3, 6].includes(champion.level);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showExpandedStats, setShowExpandedStats] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showCombos, setShowCombos] = useState(shouldAutoOpenDamageTab); // Auto-open damage tab only for specific levels
  const [activeSettingsCategory, setActiveSettingsCategory] = useState<'champion' | 'runes' | 'items' | 'skillOrder' | null>(null);


  const { gameWideAssets, championsAssets, fetchAndSetChampionAssets } = useContext(GameAssetsContext);
  const championName = champion?.name;
  
  // Update view states when champion level changes
  useEffect(() => {
    const shouldAutoOpen = defaultShowCombos ?? [1, 2, 3, 6].includes(champion.level);
    setShowCombos(shouldAutoOpen); // Only auto-open damage tab for specific levels
  }, [champion.level, defaultShowCombos]);

  // Create champion config early (before any early returns)
  const championConfig: ChampionConfig = React.useMemo(() => ({
    championId: champion.id,
    championName: champion.name,
    level: champion.level,
    team: champion.team,
    position: champion.position,
    targetPosition: champion.targetPosition || { x: 0, y: 0 },
    runes: [],
    items: [],
    runePage: champion.runePage,
    championSpecificSettings: {}
  }), [champion]);

  // Asset loading logic from original ChampionCard
  useEffect(() => {
    if (championName && !championsAssets[championName]) {
      console.log(`[StaticChampionCard] Fetching assets for ${championName}`);
      fetchAndSetChampionAssets(championName);
    }
  }, [championName, championsAssets, fetchAndSetChampionAssets]);

  // Clear active category when settings panel is closed
  useEffect(() => {
    if (!showSettings) {
      setActiveSettingsCategory(null);
    }
  }, [showSettings]);

  // Safety checks from original ChampionCard
  if (!champion || !championName) {
    console.error('[StaticChampionCard] Invalid champion data:', champion);
    return <div className="static-champion-card error">Invalid champion data</div>;
  }

  if (!championsAssets[championName]) {
    console.log('[StaticChampionCard] Loading assets for champion:', championName);
    return <div className="static-champion-card-loading">Loading {championName} assets...</div>;
  }

  // Handle missing champion stats
  if (!champion.stats || !champion.stats.totalStats) {
    console.error('[StaticChampionCard] Champion stats missing:', champion);
    return <div className="static-champion-card error">Champion stats missing</div>;
  }

  // Handle missing runePage
  if (!champion.runePage) {
    console.error('[StaticChampionCard] Champion runePage missing:', champion);
    return <div className="static-champion-card error">Champion runePage missing</div>;
  }

  // Handle missing abilities
  if (!champion.abilities) {
    console.error('[StaticChampionCard] Champion abilities missing:', champion);
    return <div className="static-champion-card error">Champion abilities missing</div>;
  }

  // Get champion assets
  const { championImageUrl, spellImageUrls, autoAttackImageUrl, passiveImageUrl } = championsAssets[championName];

  // Function to combine rune paths and runes (following ChampionCard pattern)
  const combineRunePathsAndRunes = (
    runePage: SimplifiedRunePage
  ): { primaryRunes: string[]; secondaryRunes: string[] } => {
    const primaryRunes = [runePage.primaryPath, ...runePage.primaryRunes];
    const secondaryRunes = [runePage.secondaryPath, ...runePage.secondaryRunes];
    return { primaryRunes, secondaryRunes };
  };

  const { primaryRunes, secondaryRunes } = combineRunePathsAndRunes(champion.runePage);

  // Handle quick access icon clicks
  const handleQuickAccessClick = (categoryId: 'champion' | 'runes' | 'items' | 'skillOrder') => {
    if (activeSettingsCategory === categoryId) {
      // Close if already active
      setActiveSettingsCategory(null);
      setShowSettings(false);
    } else {
      // Open this category
      setActiveSettingsCategory(categoryId);
      setShowSettings(true);
    }
  };


  // Function to render quick access icons
  const renderQuickAccessIcon = (categoryId: 'champion' | 'runes' | 'items' | 'skillOrder', iconSrc: string, alt: string) => {
    // This filter converts to #cdbe91 - the exact same color as the settings button text
    const goldFilter = 'brightness(0) saturate(100%) invert(82%) sepia(8%) saturate(1593%) hue-rotate(19deg) brightness(92%) contrast(86%)';
    
    return (
      <img 
        src={iconSrc} 
        alt={alt} 
        className={`quick-access-icon ${activeSettingsCategory === categoryId ? 'active' : ''}`}
        style={{ 
          width: '16px', 
          height: '16px',
          cursor: 'pointer',
          filter: activeSettingsCategory === categoryId ? goldFilter : 'brightness(0) saturate(100%) invert(60%)',
          transition: 'filter 0.2s ease'
        }}
        onClick={(e) => {
          e.stopPropagation(); // Prevent settings bar click
          handleQuickAccessClick(categoryId);
        }}
        title={alt}
      />
    );
  };


  const handleChangeConfig = (updatedConfig: ChampionConfig) => {
    // Handle config changes if needed
    console.log("Config changed:", updatedConfig);
  };

  const handleSaveConfig = (updatedConfig: ChampionConfig) => {
    // Handle save action if needed
    console.log("Config saved:", updatedConfig);
  };

  // Handle full champion configuration update via API
  const updateChampionConfig = async (configUpdate: {
    runes?: any;
    items?: string[];
    level?: number;
    skillOrder?: string[];
  }) => {
    try {
      console.log('Updating champion configuration via API:', configUpdate);
      
      // Use the champion ID from the prop (no separate backend initialization needed)
      const championId = champion.id;
      
      // Use the correct API base URL
      const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/static`;
      
      // Determine which specific update endpoint to call
      let endpoint = '';
      let payload: any = { championId: championId };
      
      if (configUpdate.level !== undefined) {
        endpoint = `${API_BASE_URL}/champion/update-level`;
        payload.level = configUpdate.level;
      } else if (configUpdate.items !== undefined) {
        endpoint = `${API_BASE_URL}/champion/update-items`;
        payload.items = configUpdate.items;
      } else if (configUpdate.runes !== undefined) {
        endpoint = `${API_BASE_URL}/champion/update-runes`;
        payload.runes = configUpdate.runes;
      } else if (configUpdate.skillOrder !== undefined) {
        endpoint = `${API_BASE_URL}/champion/update-skill-order`;
        payload.skillOrder = configUpdate.skillOrder;
      } else {
        throw new Error('No valid configuration update provided');
      }
      
      console.log(`Making request to ${endpoint} with payload:`, payload);
      console.log(`[StaticChampionCard] [UPDATE CHAMPION CONFIG] [PAYLOAD] [Runes]:`, payload.runes);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('Champion configuration updated successfully:', result.data);
        
        if (configUpdate.runes) {
          console.log('[StaticChampionCard] Updated champion shards from backend:', result.data.runePage?.shards);
          console.log('[StaticChampionCard] Previous champion shards:', champion.runePage.shards);
        }
        
        // Update the champion data in the parent component
        onChampionUpdate(result.data);
        
        // Show success message
        const changeType = configUpdate.runes ? 'runes' : 
                          configUpdate.items ? 'items' : 
                          configUpdate.level ? 'level' : 
                          configUpdate.skillOrder ? 'skill order' : 'configuration';
        console.log(`${changeType} updated successfully!`);
      } else {
        console.error('Failed to update champion configuration:', result.error);
        alert('Failed to update champion configuration: ' + result.error);
      }
    } catch (error) {
      console.error('Error updating champion configuration:', error);
      alert('Error updating champion configuration: ' + error);
    }
  };

  // Handle rune configuration update
  const handleRuneUpdate = async (runeSelection: any) => {
    console.log('[StaticChampionCard] handleRuneUpdate called with:', runeSelection);
    console.log('[StaticChampionCard] Current champion shards before update:', champion.runePage.shards);
    
    // Convert RuneSelection format to RuneStats format for backend compatibility
    const convertedRunes = {
      primary_style_id: parseInt(runeSelection.primaryPath),
      primary_perk_ids: runeSelection.primaryRunes.join(','),
      secondary_style_id: parseInt(runeSelection.secondaryPath),
      secondary_perk_ids: runeSelection.secondaryRunes.join(','),
      shard_offense_id: parseInt(runeSelection.shards[0]),
      shard_flex_id: parseInt(runeSelection.shards[1]),
      shard_defense_id: parseInt(runeSelection.shards[2])
    };
    
    console.log('[StaticChampionCard] Converted runes for backend:', convertedRunes);
    
    await updateChampionConfig({ runes: convertedRunes });
  };

  // Handle item configuration update
  const handleItemUpdate = async (items: string[]) => {
    await updateChampionConfig({ items });
  };

  // Handle level configuration update
  const handleLevelUpdate = async (level: number) => {
    await updateChampionConfig({ level });
  };

  // Handle skill order configuration update
  const handleSkillOrderUpdate = async (skillOrder: string[]) => {
    await updateChampionConfig({ skillOrder });
  };

  // Handle role update
  const handleRoleUpdate = async (role: string) => {
    try {
      console.log('Updating champion role via API:', role);
      
      const championId = champion.id;
      const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/static`;
      const endpoint = `${API_BASE_URL}/champion/update-role`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ championId, role })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('Champion role updated successfully:', result.data);
        onChampionUpdate(result.data);
      } else {
        console.error('Failed to update champion role:', result.error);
      }
    } catch (error) {
      console.error('Error updating champion role:', error);
    }
  };

  // Handle game time update
  const handleGameTimeUpdate = async (gameTime: number) => {
    try {
      console.log('Updating champion game time via API:', gameTime);
      
      const championId = champion.id;
      const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/static`;
      const endpoint = `${API_BASE_URL}/champion/update-game-time`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ championId, gameTime })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('Champion game time updated successfully:', result.data);
        onChampionUpdate(result.data);
      } else {
        console.error('Failed to update champion game time:', result.error);
      }
    } catch (error) {
      console.error('Error updating champion game time:', error);
    }
  };

  // Handle quest completion update
  const handleQuestCompletedUpdate = async (completed: boolean) => {
    try {
      console.log('Updating champion quest status via API:', completed);
      
      const championId = champion.id;
      const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/static`;
      const endpoint = `${API_BASE_URL}/champion/update-role-quest-completed`;
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ championId, completed })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('Champion quest status updated successfully:', result.data);
        onChampionUpdate(result.data);
      } else {
        console.error('Failed to update champion quest status:', result.error);
      }
    } catch (error) {
      console.error('Error updating champion quest status:', error);
    }
  };



  return (
    <div className={`static-champion-card ${champion.team} expanded-view`}>
      <div className="info-row">
        <div className="champion-info">
          {/* Main row contains champion image + health and abilities */}
          <div className="champion-main-row">
            <div className="champion-image-health-wrapper">
              <ChampionImageWithLevel
                imageUrl={championImageUrl}
                name={champion.name}
                level={champion.level}
              />
              {/* Always show health/mana bar */}
              <SimpleHealthManaBar
                currentHealth={champion.stats.totalStats.currentHealth || champion.stats.totalStats.health}
                maxHealth={champion.stats.totalStats.health}
                shield={champion.stats.totalStats.shield || 0}
                magicShield={champion.stats.totalStats.magicShield || 0}
                physicalShield={champion.stats.totalStats.physicalShield || 0}
                primaryResource={
                  champion.stats.primaryResource
                    ? {
                        type: champion.stats.primaryResource.type,
                        current: champion.stats.primaryResource.current,
                        max: champion.stats.primaryResource.max
                      }
                    : undefined
                }
              />
            </div>

            <div className="ability-displays-wrapper">
              <AbilityDisplays
                key={`abilities-${champion.id}-${champion.level}`}
                spellImageUrls={spellImageUrls}
                autoAttackImageUrl={autoAttackImageUrl}
                passiveImageUrl={passiveImageUrl}
                abilities={champion.abilities}
                attackRange={champion.stats?.totalStats?.attackRange}
                compactMode={false}
              />
            </div>
          </div>

          {/* Runes and items - always shown */}
          <div className="runes-items-grid">
            <div className="runes-summoners-section">
              <div className="summoner-spells">
                {champion.summonerSpells && champion.summonerSpells.length > 0 ? (
                  champion.summonerSpells.map((spell, index) => (
                    <img 
                      key={index}
                      src={getSummonerSpellImageUrl(spell)} 
                      alt={spell} 
                      className="summoner-spell-icon"
                    />
                  ))
                ) : (
                  // Fallback to default spells if none are provided
                  <>
                    <img src={getSummonerSpellImageUrl("Flash")} alt="Flash" className="summoner-spell-icon" />
                    <img src={getSummonerSpellImageUrl("Teleport")} alt="Teleport" className="summoner-spell-icon" />
                  </>
                )}
              </div>
              <div className="runes-display">
                <MinimalRuneDisplay
                  key={`runes-${champion.id}-${champion.runePage.primaryRunes.join('-')}-${champion.runePage.secondaryRunes.join('-')}-${champion.runePage.shards.join('-')}`}
                  primaryRunes={primaryRunes}
                  secondaryRunes={secondaryRunes}
                  shards={champion.runePage.shards as ShardNames[]}
                  championId={champion.id}
                  runePage={champion.runePage}
                  champion={champion}
                />
              </div>
            </div>

            <div className="items-section">
              <InventoryDisplay
                key={`items-${champion.id}-${JSON.stringify(champion.inventory.items.map(item => item.id))}-${champion.inventory.bootsSlotItem?.id || 'no-boots'}`}
                itemIds={champion.inventory.items.map((item) => item.id)}
                bootsSlotItem={champion.inventory.bootsSlotItem?.id || null}
                hasBotBootsSlot={champion.role === 'bot' && (champion.roleQuestCompleted ?? true)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Settings row - always shown */}
      <>
        {/* Settings row */}
          <div className="settings-row">
            <div className="settings-bar" onClick={(e) => {
              // Only expand/collapse if clicking on the bar itself, not on icons
              if (e.target === e.currentTarget || (e.target as Element).closest('.settings-expand-button')) {
                setShowSettings(!showSettings);
              }
            }}>
              <div className="settings-content">
                <div className="settings-expand-button" onClick={(e) => {
                  e.stopPropagation();
                  setShowSettings(!showSettings);
                }}>
                  <span className="settings-gear">⚙</span>
                  <span className="settings-title">Settings</span>
                </div>
                
                {/* Quick Access Icons */}
                <div className="quick-access-icons">
                  {renderQuickAccessIcon('runes', gameWideAssets.runeIcon, 'Runes')}
                  {renderQuickAccessIcon('items', gameWideAssets.itemIcon, 'Items')}
                  {renderQuickAccessIcon('skillOrder', gameWideAssets.skillOrderIcon, 'Skill Order')}
                  {renderQuickAccessIcon('champion', gameWideAssets.championIcon, 'Champion')}
                </div>
              </div>
            </div>

            {showSettings && (
              <CategoricalSettingsView
                key={`settings-${champion.id}-${champion.runePage.primaryRunes.join('-')}-${champion.runePage.secondaryRunes.join('-')}-${champion.runePage.shards.join('-')}-${champion.inventory.items.map(item => item.id).join('-')}`}
                championConfig={championConfig}
                champion={champion}
                onChangeConfig={handleChangeConfig}
                onSave={handleSaveConfig}
                onClose={() => {
                  // Close functionality removed - settings always shown
                  console.log("Close action triggered");
                }}
                externalActiveCategory={activeSettingsCategory}
                onCategoryChange={setActiveSettingsCategory}
                onRuneUpdate={handleRuneUpdate}
                onItemUpdate={handleItemUpdate}
                onLevelUpdate={handleLevelUpdate}
                onSkillOrderUpdate={handleSkillOrderUpdate}
                onRoleUpdate={handleRoleUpdate}
                onQuestCompletedUpdate={handleQuestCompletedUpdate}
                onGameTimeUpdate={handleGameTimeUpdate}
              />
            )}
          </div>
          
          {/* Stats Section */}
          <button className="stats-expand-button" onClick={() => setIsExpanded(!isExpanded)}>
            <span className={`stats-arrow ${isExpanded ? 'expanded' : ''}`}>▶</span>
            <span className="stats-title">Stats</span>
          </button>
          
          {isExpanded && (
            <>
              <StatsDisplay2 
                key={`stats-${champion.id}-${champion.stats.totalStats.attackDamage}`}
                champion={champion} 
                onShowExpanded={() => setShowExpandedStats(!showExpandedStats)}
                showingExpanded={showExpandedStats}
              />
              {showExpandedStats && <StatsDisplayExpanded 
                key={`stats-expanded-${champion.id}-${champion.stats.totalStats.attackDamage}`}
                champion={champion} 
              />}
            </>
          )}
      </>

      {/* Combos Section - always shown */}
      <div className="combos-header">
        <button className="combos-expand-button" onClick={() => setShowCombos(!showCombos)}>
          <span className={`combos-arrow ${showCombos ? 'expanded' : ''}`}>▶</span>
          <span className="combos-title">Damage {targetChampion ? `vs ${targetChampion.name}` : '(No Target)'}</span>
        </button>
      </div>
      {showCombos && (
        <div className="combos-panel">
          <div className="combos-content">
            {targetChampion ? (
              <StaticSimpleMatchupDisplay
                championId={champion.id}
                targetChampionId={targetChampion.id}
                championName={champion.name}
                targetChampionName={targetChampion.name}
              />
            ) : (
              <div className="no-target-message">
                <p>No target champion available for combo calculations.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StaticChampionCard; 